import { withDevtools } from '@angular-architects/ngrx-toolkit';
import { computed, effect, inject } from '@angular/core';
import {
  INIT_MODES,
  IReportTabState,
  InitMode,
  StockState,
  StockTankFormState,
  StockTanks,
  TankTransferFormState,
  TankTransfersState,
} from '@navstation-portal/reporting/data-model/vessel-report';
import { SelectOption } from '@navstation-portal/shared/data-model/common';
import { VesselSpecStore } from '@navstation-portal/vessels/data-access/store';
import { Tank } from '@navstation-portal/vessels/data-model/vessel';
import { patchState, signalStore, withComputed, withHooks, withMethods, withState } from '@ngrx/signals';
import { initStateField } from '../../utils/store.utility';
import { withFormStoreReducer } from '../with-form-store-reducer';
import { stockEvents } from './stock-events';
import { buildStockVm } from './stock-vm-builder';
import { withStockEffects } from './with-stock-effects';

const initStockStateReducer = (initialData: IReportTabState, initMode: InitMode) => {
  const data = initialData as StockState;

  switch (initMode) {
    case INIT_MODES.CARRY_OVER:
      return initWithCarryOver(data);
    case INIT_MODES.IMPORT:
      return initWithExistingReport(data);
    case INIT_MODES.BLANK:
      return initBlank();
    default:
      return initBlank();
  }
};
const initWithCarryOver = (data: StockState) => {
  return {
    formState: {
      options: {
        tankSounding: initStateField<boolean>(),
        // The template carries it from the report before.
        tankBasedReporting: initStateField<boolean>(),
      },

      // V1 carries no transfer to a new report.
      tankTransfers: {
        ...tankTransfersArray(),
      },
      fuelWaterRob: {
        bilgeWater: initStateField<number>(),
        dischargedSludge: initStateField<number>(),
        dischargedBilgeWater: initStateField<number>(),
        freshWater: initStateField<number>(),
        distilledWater: initStateField<number>(),
        tankCleaningChemical: initStateField<number>(),
      },
      lubeOilRob: {
        lubeOilSystem: initStateField<number>(),
        lubeOilSpare: initStateField<number>(),
        lowBnCylinderOil: initStateField<number>(),
        highBnCylinderOil: initStateField<number>(),
        aeLubeOil: initStateField<number>(),
        turboOil: initStateField<number>(),
        hydraulicOil: initStateField<number>(),
        otherOil: initStateField<number>(),
      },
      oilLosses: {
        totalWasteOil: initStateField<number>(),
        incineratedWasteOil: initStateField<number>(),
        evaporatedWaterFromWasteOil: initStateField<number>(),
        sludgeRob: initStateField<number>(),
        dischargedWasteOil: initStateField<number>(),
      },
      tanks: {
        ...tankReportingArray(data?.tanks),
      },
    },
  };
};
const initWithExistingReport = (data: StockState) => {
  return {
    formState: {
      options: {
        tankSounding: initStateField<boolean>(data.options?.tankSounding),
        tankBasedReporting: initStateField<boolean>(data.options?.tankBasedReporting),
      },
      tankTransfers: {
        ...tankTransfersArrayFromExisting(data.tankTransfers),
      },
      fuelWaterRob: {
        bilgeWater: initStateField<number>(data.fuelWaterRob?.bilgeWater),
        dischargedSludge: initStateField<number>(data.fuelWaterRob?.dischargedSludge),
        dischargedBilgeWater: initStateField<number>(data.fuelWaterRob?.dischargedBilgeWater),
        freshWater: initStateField<number>(data.fuelWaterRob?.freshWater),
        distilledWater: initStateField<number>(data.fuelWaterRob?.distilledWater),
        tankCleaningChemical: initStateField<number>(data.fuelWaterRob?.tankCleaningChemical),
      },
      lubeOilRob: {
        lubeOilSystem: initStateField<number>(data.lubeOilRob?.lubeOilSystem),
        lubeOilSpare: initStateField<number>(data.lubeOilRob?.lubeOilSpare),
        lowBnCylinderOil: initStateField<number>(data.lubeOilRob?.lowBnCylinderOil),
        highBnCylinderOil: initStateField<number>(data.lubeOilRob?.highBnCylinderOil),
        aeLubeOil: initStateField<number>(data.lubeOilRob?.aeLubeOil),
        turboOil: initStateField<number>(data.lubeOilRob?.turboOil),
        hydraulicOil: initStateField<number>(data.lubeOilRob?.hydraulicOil),
        otherOil: initStateField<number>(data.lubeOilRob?.otherOil),
      },
      oilLosses: {
        totalWasteOil: initStateField<number>(data.oilLosses?.totalWasteOil),
        incineratedWasteOil: initStateField<number>(data.oilLosses?.incineratedWasteOil),
        evaporatedWaterFromWasteOil: initStateField<number>(data.oilLosses?.evaporatedWaterFromWasteOil),
        sludgeRob: initStateField<number>(data.oilLosses?.sludgeRob),
        dischargedWasteOil: initStateField<number>(data.oilLosses?.dischargedWasteOil),
      },
      tanks: {
        ...tankReportingArray(data?.tanks),
      },
    },
  };
};

const tankReportingArray = (data?: StockTanks | undefined) => {
  if (!data)
    return {
      tankRows: [] as StockTankFormState[],
    };

  return {
    tankRows: generateTankReportingArray(data),
  };
};

const generateTankReportingArray = (data: StockTanks) => {
  if (!data) return [];
  const stockTankEntries = Object.entries(data) as [string, StockTanks[string]][];

  const result: StockTankFormState[] = stockTankEntries.map(([key, tank]) => ({
    id: initStateField<string | undefined>(key),
    _id: initStateField<string | undefined>(tank?._id.$oid),
    amount: initStateField<number | undefined>(tank?.amount),
    amountBeforeBunkering: initStateField<number | undefined>(tank?.amountBeforeBunkering),
    bdnNumber: initStateField<string | undefined>(tank?.bdnNumber),
    bunkerDate: initStateField<Date>(tank?.bunkerDate),
    bunkerPort: initStateField<string | undefined>(tank?.bunkerPort),
    density: initStateField<number | undefined>(tank?.density),
    densityBeforeBunkering: initStateField<number | undefined>(tank?.densityBeforeBunkering),
    fuelClass: initStateField<string | undefined>(tank?.fuelClass),
    fuelGrade: initStateField<SelectOption | undefined>(tank?.fuelGrade),
    fuelGradeAddedInTank: initStateField<SelectOption | undefined>(tank?.fuelGradeAddedInTank),
    groupColor: initStateField<string | undefined>(undefined),
    inUse: initStateField<boolean | undefined>(tank?.inUse),
    isBlend: initStateField<boolean | undefined>(tank?.isBlend),
    index: initStateField<number | undefined>(undefined),
    lcv: initStateField<number | undefined>(tank?.lcv),
    lcvBeforeBunkering: initStateField<number | undefined>(tank?.lcvBeforeBunkering),
    stableUntil: initStateField<Date>(tank?.stableUntil),
    sulphurBeforeBunkering: initStateField<number | undefined>(tank?.sulphurBeforeBunkering),
    sulphurAfterBunkering: initStateField<number | undefined>(tank?.sulphurAfterBunkering),
    tankCapacity: initStateField<number | undefined>(),
    tankName: initStateField<string | undefined>(),
    quantityAddedInTank: initStateField<number | undefined>(tank?.quantityAddedInTank),
    viscosity: initStateField<number | undefined>(tank?.viscosity),
  }));

  return result;
};

// V1 adds empty rows until the report holds this many transfers
// (initializeReport). The section has no Add button, so these are the rows the
// user fills in.
const MIN_TANK_TRANSFER_ROWS = 4;

const emptyTankTransfer = (): TankTransferFormState => ({
  from: initStateField<SelectOption | undefined>(),
  to: initStateField<SelectOption | undefined>(),
  quantity: initStateField<number | undefined>(),
});

const withMinimumTankTransferRows = (transfers: TankTransferFormState[]): TankTransferFormState[] => [
  ...transfers,
  ...Array.from({ length: Math.max(0, MIN_TANK_TRANSFER_ROWS - transfers.length) }, emptyTankTransfer),
];

const tankTransfersArray = () => {
  return {
    transfers: withMinimumTankTransferRows([]),
  };
};

const tankTransfersArrayFromExisting = (data?: TankTransfersState) => {
  return {
    transfers: withMinimumTankTransferRows(data ? generateTankTransfersArrayFromExisting(data) : []),
  };
};

const generateTankTransfersArrayFromExisting = (data: TankTransfersState) => {
  const result: TankTransferFormState[] = data.map((transfer) => ({
    from: initStateField<SelectOption | undefined>(
      transfer.from
        ? {
            name: transfer.from,
            code: transfer.from,
          }
        : undefined
    ),
    to: initStateField<SelectOption | undefined>(
      transfer.to
        ? {
            name: transfer.to,
            code: transfer.to,
          }
        : undefined
    ),
    quantity: initStateField<number | undefined>(transfer.quantity),
  }));
  return result;
};

const initBlank = () => {
  return {
    baselineAccumulators: null as Map<string, { sulphur: number; lcv: number; density: number; amount: number }> | null,
    formState: {
      options: {
        tankSounding: initStateField<boolean>(),
        tankBasedReporting: initStateField<boolean>(),
      },

      tankTransfers: {
        ...tankTransfersArray(),
      },
      fuelWaterRob: {
        bilgeWater: initStateField<number>(),
        dischargedSludge: initStateField<number>(),
        dischargedBilgeWater: initStateField<number>(),
        freshWater: initStateField<number>(),
        distilledWater: initStateField<number>(),
        tankCleaningChemical: initStateField<number>(),
      },
      lubeOilRob: {
        lubeOilSystem: initStateField<number>(),
        lubeOilSpare: initStateField<number>(),
        lowBnCylinderOil: initStateField<number>(),
        highBnCylinderOil: initStateField<number>(),
        aeLubeOil: initStateField<number>(),
        turboOil: initStateField<number>(),
        hydraulicOil: initStateField<number>(),
        otherOil: initStateField<number>(),
      },
      oilLosses: {
        totalWasteOil: initStateField<number>(),
        incineratedWasteOil: initStateField<number>(),
        evaporatedWaterFromWasteOil: initStateField<number>(),
        sludgeRob: initStateField<number>(),
        dischargedWasteOil: initStateField<number>(),
      },
      tanks: {
        ...tankReportingArray(),
      },
    },
  };
};
export const StockStore = signalStore(
  { providedIn: 'root' },
  withDevtools('StockStore'),
  withState(initBlank()),
  withMethods((store) => {
    return {
      sortTanksByVesselSpec(tanksToSort: StockTankFormState[], vesselSpecTanks: Tank[]) {
        const tankTransfers = store.formState().tankTransfers?.transfers || [];

        if (!tanksToSort.length || !vesselSpecTanks.length) {
          patchState(store, (state) => ({
            formState: {
              ...state.formState,
              tanks: { tankRows: tanksToSort },
            },
          }));
          return;
        }

        const tankMap = new Map(tanksToSort.map((t) => [t.id.value?.toString(), t]));

        const sortedTanks = vesselSpecTanks
          .map((spec) => {
            const tank = tankMap.get(spec.tank_id.$oid);
            if (!tank) return undefined;

            return {
              ...tank,
              tankName: initStateField(spec.name),
              tankCapacity: initStateField(spec.volume_ratings?.[0]?.value),
            };
          })
          .filter(Boolean)
          // A cell key names the position of its row here. The table leaves
          // the hidden tanks out, so each row carries its position.
          .map((tank, position) => ({ ...tank, index: initStateField(position) })) as StockTankFormState[];
        const tanksWithColors = setTankGroupColors(sortedTanks);
        const normalizedTransfers = normalizeTankTransfers(tankTransfers, vesselSpecTanks);

        patchState(store, (state) => ({
          formState: {
            ...state.formState,
            tanks: { tankRows: tanksWithColors },
            tankTransfers: {
              ...state.formState.tankTransfers,
              transfers: normalizedTransfers,
            },
          },
        }));
      },
    };
  }),

  withHooks((store) => ({
    onInit: () => {
      const vesselSpecStore = inject(VesselSpecStore);
      let lastTransfersHash = '';

      effect(() => {
        const vesselSpec = vesselSpecStore.vesselSpec();
        const tanks = store.formState().tanks?.tankRows || [];
        const needsSorting = tanks.length > 0 && !tanks.some((t) => t.groupColor?.value);
        if (vesselSpec?.tanks && needsSorting) {
          store.sortTanksByVesselSpec(tanks, vesselSpec.tanks);
        }
      });

      effect(() => {
        const transfers = store.formState().tankTransfers?.transfers || [];
        const tanks = store.formState().tanks?.tankRows || [];

        const transfersHash = transfers
          .map((t) => `${t.from.value?.name || ''}-${t.to.value?.name || ''}-${t.quantity.value || 0}`)
          .join('|');

        if (transfersHash === lastTransfersHash) {
          return;
        }
        lastTransfersHash = transfersHash;

        if (transfers.length === 0) {
          if (store.baselineAccumulators()) {
            patchState(store, { baselineAccumulators: null });
          }
          return;
        }

        let baseline = store.baselineAccumulators();
        if (!baseline) {
          baseline = buildBaselineAccumulators(tanks);
          patchState(store, { baselineAccumulators: baseline });
        }
        const tankAccumulators = applyTransfers(baseline, transfers);
        const resultsMap = createResultsMap(tanks, baseline, tankAccumulators);

        const updatedTanks = tanks.map((tank) => {
          const result = resultsMap.get(tank.tankName.value || '');
          const baselineValue = baseline.get(tank.tankName.value || '');

          if (result) {
            return {
              ...tank,
              sulphurAfterBunkering: initStateField(result.sulphur),
              lcv: initStateField(result.lcv),
              density: initStateField(result.density),
            };
          } else if (baselineValue) {
            const needsRevert =
              tank.sulphurAfterBunkering?.value !== baselineValue.sulphur ||
              tank.lcv?.value !== baselineValue.lcv ||
              tank.density?.value !== baselineValue.density;

            if (needsRevert) {
              return {
                ...tank,
                sulphurAfterBunkering: initStateField(baselineValue.sulphur),
                lcv: initStateField(baselineValue.lcv),
                density: initStateField(baselineValue.density),
              };
            }
          }

          return tank;
        });

        patchState(store, (state) => ({
          formState: {
            ...state.formState,
            tanks: {
              tankRows: updatedTanks,
            },
          },
        }));
      });
    },
  })),
  withStockEffects(),
  withFormStoreReducer(stockEvents, initStockStateReducer, 'stock'),
  withComputed((store, vesselSpecStore = inject(VesselSpecStore)) => ({
    vm: computed(() => buildStockVm(store.formState())),
    // The rows the tank tables draw. As in V1, a tank that the vessel spec
    // hides gets no row, and the form state keeps its data for the save.
    stockTableVM: computed(() => {
      const tanks = store.formState().tanks?.tankRows || [];
      const hasSortedTanks = tanks.length > 0 && tanks.some((t) => t.groupColor?.value);
      if (!hasSortedTanks) return [];

      const hiddenTankIds = new Set(
        (vesselSpecStore.vesselSpec()?.tanks ?? []).filter((tank) => tank.hide_tank).map((tank) => tank.tank_id.$oid)
      );
      return tanks.filter((tank) => !hiddenTankIds.has(tank.id.value?.toString() ?? ''));
    }),
  }))
);

function createResultsMap(
  tanks: StockTankFormState[],
  baseline: Map<string, { sulphur: number; lcv: number; density: number }>,
  tankAccumulators: Map<string, { sulphur: number; lcv: number; density: number }>
) {
  return tanks.reduce((resultMap, tank) => {
    const accum = baseline.get(tank.tankName.value || '');
    const afterTransferAccum = tankAccumulators.get(tank.tankName.value || '');
    if (!accum || !afterTransferAccum) return resultMap;

    if (
      afterTransferAccum.sulphur !== accum.sulphur ||
      afterTransferAccum.lcv !== accum.lcv ||
      afterTransferAccum.density !== accum.density
    ) {
      resultMap.set(tank.tankName.value || '', {
        sulphur: afterTransferAccum.sulphur,
        lcv: afterTransferAccum.lcv,
        density: afterTransferAccum.density,
      });
    }

    return resultMap;
  }, new Map<string, { sulphur: number; lcv: number; density: number }>());
}

function applyTransfers(
  initialAccumulators: Map<string, { sulphur: number; lcv: number; density: number; amount: number }>,
  transfers: TankTransferFormState[]
) {
  const accumulatorsCopy = new Map(
    Array.from(initialAccumulators.entries()).map(([key, value]) => [key, { ...value }])
  );

  return transfers.reduce((accumulators, transfer) => {
    if (!transfer.from.value?.name || !transfer.to.value?.name || !transfer.quantity.value) {
      return accumulators;
    }

    const fromAccum = accumulators.get(transfer.from.value.name);
    const toAccum = accumulators.get(transfer.to.value.name);
    if (!fromAccum || !toAccum) return accumulators;

    const qty = transfer.quantity.value;
    const total = toAccum.amount + qty;

    if (total > 0) {
      accumulators.set(transfer.to.value.name, {
        sulphur:
          Math.round(((toAccum.amount / total) * toAccum.sulphur + (qty / total) * fromAccum.sulphur) * 100) / 100,
        lcv: Math.round(((toAccum.amount / total) * toAccum.lcv + (qty / total) * fromAccum.lcv) * 100) / 100,
        density:
          Math.round(((toAccum.amount / total) * toAccum.density + (qty / total) * fromAccum.density) * 100) / 100,
        amount: total,
      });
    }

    return accumulators;
  }, accumulatorsCopy);
}

function buildBaselineAccumulators(tanks: StockTankFormState[]) {
  return new Map(
    tanks.map((tank) => [
      tank.tankName.value || '',
      {
        sulphur: tank.sulphurAfterBunkering?.value || 0,
        lcv: tank.lcv?.value || 0,
        density: tank.density?.value || 0,
        amount: tank.amount.value || 0,
      },
    ])
  );
}

function setTankGroupColors(tanks: StockTankFormState[]) {
  const colors = ['#b7c1fb', '#ff5310', '#bed060', '#348adf', '#ffdd7f', '#f7bfad'].reverse();

  const colorMap = new Map<string, string>();
  let colorIndex = 0;
  return tanks.map((tank) => {
    const bunkerDate = tank.bunkerDate?.value ? new Date(tank.bunkerDate.value).toString() : '';
    const bunkerPort = tank.bunkerPort?.value || '';
    const fuelGrade = tank.fuelGrade?.value || '';
    const key = `${bunkerDate}|${bunkerPort}|${fuelGrade}`;

    if (bunkerDate && bunkerPort && fuelGrade) {
      if (!colorMap.has(key)) {
        colorMap.set(key, colors[colorIndex % colors.length]);
        colorIndex++;
      }

      return {
        ...tank,
        groupColor: initStateField(colorMap.get(key) || ''),
      };
    }

    return {
      ...tank,
      groupColor: initStateField(''),
    };
  });
}

function normalizeTankTransfers(transfers: TankTransferFormState[], vesselSpecTanks: Tank[]) {
  return transfers.map((transfer) => ({
    ...transfer,
    from: initStateField(resolveTankTransferOption(transfer.from.value, vesselSpecTanks)),
    to: initStateField(resolveTankTransferOption(transfer.to.value, vesselSpecTanks)),
  }));
}

function resolveTankTransferOption(value: SelectOption | undefined, vesselSpecTanks: Tank[]) {
  if (!value) return undefined;

  const code = value.code;
  const matchedTank = vesselSpecTanks.find((tank) => {
    if (typeof code === 'object' && code && '$oid' in code) {
      return tank.tank_id.$oid === code.$oid;
    }

    if (typeof code === 'string') {
      return tank.tank_id.$oid === code || tank.name === code;
    }

    return tank.name === value.name;
  });

  if (!matchedTank) {
    return undefined;
  }

  return {
    name: matchedTank.name,
    code: matchedTank.tank_id,
  };
}
